import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { ArrowLeft, Play, Trash2, Save } from 'lucide-react';
import { Input } from './ui/input';
import { toast } from 'sonner';

export default function WalkingHistory({ history, onBack, onLoadRoutine }) {
  const [savedRoutines, setSavedRoutines] = useState([]);
  const [routineName, setRoutineName] = useState('');
  const [showSaveForm, setShowSaveForm] = useState(false);
  const [currentConfig, setCurrentConfig] = useState({
    quickWalkingTime: 180,
    slowWalkingTime: 180,
    quickWalkingSets: 4,
    slowWalkingSets: 4
  });

  useEffect(() => {
    const saved = localStorage.getItem('gym_notebook_walking_routines');
    if (saved) {
      setSavedRoutines(JSON.parse(saved));
    }
  }, []);

  const handleSaveRoutine = () => {
    if (!routineName.trim()) {
      toast.error('Please enter a routine name');
      return;
    }
    
    const newRoutine = {
      id: Date.now(),
      name: routineName,
      ...currentConfig
    };
    const updated = [...savedRoutines, newRoutine];
    setSavedRoutines(updated);
    localStorage.setItem('gym_notebook_walking_routines', JSON.stringify(updated));
    toast.success('Walking routine saved!');
    setRoutineName('');
    setShowSaveForm(false);
  };

  const handleDeleteRoutine = (id) => {
    const updated = savedRoutines.filter(r => r.id !== id);
    setSavedRoutines(updated);
    localStorage.setItem('gym_notebook_walking_routines', JSON.stringify(updated));
    toast.success('Routine deleted');
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <Button
        variant="ghost"
        onClick={onBack}
        className="mb-4"
        data-testid="walking-history-back-btn"
      >
        <ArrowLeft className="mr-2 h-4 w-4" />
        Back to Setup
      </Button>

      <div className="text-center space-y-2 py-4">
        <h2 className="text-2xl font-bold tracking-tight text-cyan-400">Saved Walking Routines</h2>
        <p className="text-muted-foreground">Load or manage your walking workout routines</p>
      </div>

      {/* Saved Routines */}
      <Card>
        <CardHeader>
          <CardTitle>Your Routines</CardTitle>
          <CardDescription>Quick start from saved configurations</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {savedRoutines.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <p>No saved routines yet</p>
              <p className="text-sm mt-2">Configure and save a walking routine from the setup screen</p>
            </div>
          ) : (
            <div className="space-y-3">
              {savedRoutines.map((routine) => (
                <div
                  key={routine.id}
                  className="p-4 border border-border rounded-lg hover:border-cyan-500/50 transition-colors"
                >
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-lg">{routine.name}</h3>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDeleteRoutine(routine.id)}
                      className="h-8 w-8 text-red-400 hover:text-red-300 hover:bg-red-500/20"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm text-muted-foreground mb-4">
                    <div>Quick Walking: <span className="text-cyan-400 font-medium">{routine.quickWalkingTime / 60}min × {routine.quickWalkingSets}</span></div>
                    <div>Slow Walking: <span className="text-green-400 font-medium">{routine.slowWalkingTime / 60}min × {routine.slowWalkingSets}</span></div>
                  </div>
                  <Button
                    onClick={() => onLoadRoutine(routine)}
                    className="w-full bg-gradient-to-r from-cyan-500 to-green-500 hover:from-cyan-600 hover:to-green-600"
                  >
                    <Play className="mr-2 h-4 w-4" />
                    Start Routine
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recent Workouts History */}
      {history && history.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Recent Workouts</CardTitle>
            <CardDescription>Your walking workout history</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {history.slice(0, 10).map((workout, index) => (
                <div
                  key={workout.id || index}
                  className="p-3 bg-muted/30 rounded-lg"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium">Walking Workout</div>
                      <div className="text-xs text-muted-foreground">
                        {new Date(workout.date).toLocaleDateString()} at {new Date(workout.date).toLocaleTimeString()}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-mono text-cyan-400">{formatTime(workout.duration)}</div>
                      <div className="text-xs text-muted-foreground">
                        {workout.quickSets + workout.slowSets} intervals
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
